/**
 * Demo mode — suppresses error toasts during client-facing demos.
 *
 * Activated by ANY of these (priority order):
 *   1. URL query param   `?demo=1` (or any truthy value)
 *   2. localStorage flag `truspace.demoMode = 'true'`
 *   3. Env var           `VITE_DEMO_MODE=true` at build time
 *
 * When active:
 *   - `toast.error(...)` calls become no-ops (silent)
 *   - 403 / 5xx auto-toasts in the API client are suppressed
 *   - Suppressed errors are still logged to `console.warn` so devs can
 *     debug after the demo without losing diagnostic info
 *
 * Form validation errors, inline page errors, and the React error
 * boundary are NOT affected — those remain visible because they're
 * intentional UX, not unexpected popups.
 *
 * To enable for a single demo session, just add `?demo=1` to the URL.
 * To enable persistently in this browser:
 *   localStorage.setItem('truspace.demoMode', 'true')
 * To disable:
 *   localStorage.removeItem('truspace.demoMode')
 */

const STORAGE_KEY = 'truspace.demoMode';
const QUERY_KEYS = ['demo', 'demoMode', 'demo_mode'];

let cached: boolean | null = null;

/**
 * Returns true if demo mode is active for the current page load.
 * Result is cached after first call so the URL/localStorage are read
 * once per page load (cheap, but no need to re-read on every toast).
 */
export function isDemoMode(): boolean {
  if (cached !== null) return cached;

  // 1. URL query param — highest priority, easy to flip per session
  if (typeof window !== 'undefined' && window.location?.search) {
    const params = new URLSearchParams(window.location.search);
    for (const key of QUERY_KEYS) {
      const v = params.get(key);
      if (v !== null && v !== '0' && v.toLowerCase() !== 'false') {
        cached = true;
        // Also persist for the rest of the session so reloads / nav within
        // the SPA don't lose the flag.
        try {
          window.sessionStorage?.setItem(STORAGE_KEY, 'true');
        } catch {
          /* ignore storage errors */
        }
        return cached;
      }
    }
  }

  // 2. sessionStorage (set by query param above) or localStorage
  if (typeof window !== 'undefined') {
    try {
      if (
        window.sessionStorage?.getItem(STORAGE_KEY) === 'true' ||
        window.localStorage?.getItem(STORAGE_KEY) === 'true'
      ) {
        cached = true;
        return cached;
      }
    } catch {
      /* ignore storage errors */
    }
  }

  // 3. Build-time env var
  if (import.meta.env?.VITE_DEMO_MODE === 'true') {
    cached = true;
    return cached;
  }

  cached = false;
  return cached;
}

/** Reset the cache — useful in tests. */
export function _resetDemoModeCache(): void {
  cached = null;
}
